import React from 'react';
import { Wrench, Phone, MessageSquare, Calendar, Star, FileText, Users, Clock, CheckCircle2 } from 'lucide-react';

const KeyFeaturesPlain = () => {
  return (
    <div className="bg-gradient-to-br from-slate-900/50 to-slate-800/50 rounded-2xl p-8 border-2 border-slate-700">
      
      <div className="text-center mb-8">
        <h3 className="text-3xl md:text-4xl font-black text-white mb-4">
          What's Actually Inside The System?
        </h3>
        <p className="text-xl text-gray-300">
          No tech jargon. Here's what you actually get, in plain English.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-6 mb-10">
        
        {/* Feature 1 */}
        <div className="bg-white/5 backdrop-blur rounded-xl p-6 border border-white/10">
          <div className="flex items-start mb-4">
            <div className="bg-blue-500/20 p-3 rounded-lg mr-4">
              <Phone className="text-blue-400" size={28} />
            </div>
            <div>
              <h4 className="text-xl font-bold text-white mb-2">AI Phone Answering (Optional)</h4>
              <p className="text-gray-300 text-sm">
                When you're under a sink or can't answer, AI picks up. Takes their details, books them in your calendar, sends confirmation text. 
                You can turn this on/off anytime. Or just use preset text replies instead.
              </p>
            </div>
          </div>
        </div>

        {/* Feature 2 */}
        <div className="bg-white/5 backdrop-blur rounded-xl p-6 border border-white/10">
          <div className="flex items-start mb-4">
            <div className="bg-green-500/20 p-3 rounded-lg mr-4">
              <MessageSquare className="text-green-400" size={28} />
            </div>
            <div>
              <h4 className="text-xl font-bold text-white mb-2">Missed Call Text-Back</h4>
              <p className="text-gray-300 text-sm">
                You missed a call? System texts them within 60 seconds: "Sorry I missed you! I'll call back in 30 mins" or books them in automatically. 
                No more losing customers to whoever answers first.
              </p>
            </div>
          </div>
        </div>

        {/* Feature 3 */}
        <div className="bg-white/5 backdrop-blur rounded-xl p-6 border border-white/10">
          <div className="flex items-start mb-4">
            <div className="bg-purple-500/20 p-3 rounded-lg mr-4">
              <FileText className="text-purple-400" size={28} />
            </div>
            <div>
              <h4 className="text-xl font-bold text-white mb-2">Instant Quote Sending</h4>
              <p className="text-gray-300 text-sm">
                You finish a job, quote them £850 for a new boiler. System emails them a professional quote with your logo, payment options, booking link. 
                All whilst you're driving to the next job.
              </p>
            </div>
          </div>
        </div>

        {/* Feature 4 */}
        <div className="bg-white/5 backdrop-blur rounded-xl p-6 border border-white/10">
          <div className="flex items-start mb-4">
            <div className="bg-orange-500/20 p-3 rounded-lg mr-4">
              <Calendar className="text-orange-400" size={28} />
            </div>
            <div>
              <h4 className="text-xl font-bold text-white mb-2">Smart Calendar & Reminders</h4>
              <p className="text-gray-300 text-sm">
                Customers book themselves online. System sends them reminders the day before. They confirm or reschedule with one tap. 
                No more no-shows. No more double bookings.
              </p>
            </div>
          </div>
        </div>

        {/* Feature 5 */}
        <div className="bg-white/5 backdrop-blur rounded-xl p-6 border border-white/10">
          <div className="flex items-start mb-4">
            <div className="bg-yellow-500/20 p-3 rounded-lg mr-4">
              <Star className="text-yellow-400" size={28} />
            </div>
            <div>
              <h4 className="text-xl font-bold text-white mb-2">Automatic Review Requests</h4>
              <p className="text-gray-300 text-sm">
                You finish a job. 2 hours later, system texts them: "How did we do? Leave a review?" 
                67% do it. Your Google ranking goes up. More people find you. More jobs.
              </p>
            </div>
          </div>
        </div>

        {/* Feature 6 */}
        <div className="bg-white/5 backdrop-blur rounded-xl p-6 border border-white/10">
          <div className="flex items-start mb-4">
            <div className="bg-cyan-500/20 p-3 rounded-lg mr-4">
              <Clock className="text-cyan-400" size={28} />
            </div>
            <div>
              <h4 className="text-xl font-bold text-white mb-2">7-Stage Follow-Up System</h4>
              <p className="text-gray-300 text-sm">
                You quoted someone £2,400 for a bathroom refit. They said "I'll think about it." System follows up 7 times over 2 weeks automatically. 
                Text, email, reminders. Most competitors give up after one try.
              </p>
            </div>
          </div>
        </div>

        {/* Feature 7 */}
        <div className="bg-white/5 backdrop-blur rounded-xl p-6 border border-white/10">
          <div className="flex items-start mb-4">
            <div className="bg-pink-500/20 p-3 rounded-lg mr-4">
              <Users className="text-pink-400" size={28} />
            </div>
            <div>
              <h4 className="text-xl font-bold text-white mb-2">Team Management (Up to 50)</h4>
              <p className="text-gray-300 text-sm">
                Your apprentice sees their jobs on their phone. Your mate helping out can log in. Everyone sees the same calendar. 
                No more "I didn't know I was meant to be there."
              </p>
            </div>
          </div>
        </div>

        {/* Feature 8 */}
        <div className="bg-white/5 backdrop-blur rounded-xl p-6 border border-white/10">
          <div className="flex items-start mb-4">
            <div className="bg-red-500/20 p-3 rounded-lg mr-4">
              <Wrench className="text-red-400" size={28} />
            </div>
            <div>
              <h4 className="text-xl font-bold text-white mb-2">Job Tracking & Notes</h4>
              <p className="text-gray-300 text-sm">
                Take photos of the job on your phone. Add notes: "Need 15mm pipe." System stores everything. 
                Next time they call, you know exactly what you did 2 years ago.
              </p>
            </div>
          </div>
        </div>

      </div>

      {/* Who Is This For Section - Single Column */}
      <div className="border-t-2 border-white/10 pt-8">
        <h3 className="text-3xl font-bold text-white mb-6 text-center">
          Not Sure If This Is For You?
        </h3>

        <div className="max-w-3xl mx-auto">
          
          {/* This IS For You */}
          <div className="bg-green-900/20 border-2 border-green-500/50 rounded-xl p-8 mb-6">
            <h4 className="text-2xl font-bold text-green-400 mb-6 flex items-center justify-center">
              <CheckCircle2 className="mr-3" size={28} />
              This IS For You If:
            </h4>
            <div className="space-y-3 text-gray-200 text-lg">
              <p>✅ You're missing calls whilst you're on jobs</p>
              <p>✅ You're doing over £5,000/month in work</p>
              <p>✅ You've got quotes sitting that you haven't followed up on</p>
              <p>✅ You're working evenings/weekends answering calls</p>
              <p>✅ You want to grow but can't keep up with admin</p>
              <p>✅ You've got (or want) an apprentice or helper</p>
              <p>✅ You're tired of no-shows messing up your day</p>
              <p>✅ You want more Google reviews but keep forgetting to ask</p>
              <p>✅ You're missing family time because of work</p>
            </div>
          </div>

          {/* This Is NOT For You - Comedy Version */}
          <div className="bg-red-900/20 border-2 border-red-500/50 rounded-xl p-8 mb-6">
            <h4 className="text-2xl font-bold text-red-400 mb-6 flex items-center justify-center">
              <Clock className="mr-3" size={28} />
              This Is NOT For You If:
            </h4>
            <div className="space-y-3 text-gray-200 text-lg">
              <p>❌ You genuinely enjoy working 70-hour weeks for the rest of your life</p>
              <p>❌ You prefer missing calls and losing money to competitors</p>
              <p>❌ You think "admin work" is a fun hobby you'd hate to automate</p>
              <p>❌ You love the thrill of no-shows ruining your perfectly planned day</p>
              <p>❌ You're allergic to making more money whilst working less</p>
              <p>❌ You believe spending time with family is overrated</p>
              <p>❌ You're retiring next week (fair enough, enjoy it!)</p>
              <p>❌ You still use a Nokia 3310 and refuse to upgrade</p>
            </div>
          </div>

          {/* Bottom CTA */}
          <div className="bg-blue-900/30 border border-blue-500/30 rounded-xl p-6 text-center">
            <p className="text-xl text-white font-bold mb-2">
              Still Not Sure?
            </p>
            <p className="text-gray-300 text-lg">
              Look, if you're reading this far, it's probably for you.<br />
              Try it for 14 days for £5. If it's not for you, you get a full refund.<br />
              <span className="text-blue-400 font-bold">You risk £5. You could gain £3,227/month. That's a pretty good bet.</span>
            </p>
          </div>

        </div>
      </div>

    </div>
  );
};

export default KeyFeaturesPlain;
